import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Typography,
  Card,
  Row,
  Col,
  Space,
  Divider,
  Statistic,
  Tag,
  Tooltip,
  Select,
  message,
  Checkbox,
  Empty,
  Spin,
  Modal,
  InputNumber,
  Alert,
} from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, ToolOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import { catMatches } from '../../utils/searchNorm';
import { highlightText } from '../../components/Highlight';
import { getApprovableLines, createPO, costCenterLabel, requestMRLineCancel, decideMRLineCancel, refineMRLine, createVendorOrder } from '../../api';
import PathChoice from '../../components/receiving/PathChoice';
import DirectPurchase, { DirectLine } from '../../components/receiving/DirectPurchase';
import ResponsiveTable from '../../components/responsive/ResponsiveTable';
import POCreatePhone from './POCreatePhone';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { PrefillInfo, usualPrice, historyHint } from '../../utils/priceSource';
import { useAuth } from '../../context/AuthContext';
import RefineLineModal from '../../components/RefineLineModal';
import { ItemCell, VendorCell } from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** A line with no catalog code: a stated gap where the code chip would sit. */
const freeTextMark = (
  <span
    style={{
      display: 'inline-block',
      padding: '0 6px',
      fontSize: 11,
      lineHeight: '18px',
      color: '#8a94a6',
      border: '1px dashed #c9cede',
      borderRadius: 4,
      whiteSpace: 'nowrap',
    }}
  >
    free text
  </span>
);

/** Code · EN · AR in one cell; free-text lines say so instead of showing an empty code. */
const renderItem = (line: {
  partNumber?: string;
  itemDescription: string;
  catalogItem?: { id?: number; partNumber: string; description?: string; descriptionAr?: string | null } | null;
}, highlight?: string, pictureUrl?: string | null, onPictureError?: () => void) => {
  const code = line.catalogItem?.partNumber || line.partNumber;
  const ciId = line.catalogItem?.id ?? null;
  const cell = (
    <ItemCell
      partNumber={code}
      description={line.catalogItem?.description || line.itemDescription}
      descriptionAr={line.catalogItem?.descriptionAr}
      linkTo={line.catalogItem?.id ? `/catalog/${line.catalogItem.id}` : undefined}
      highlight={highlight}
      showThumb
      pictureUrl={pictureUrl}
      thumbItemId={ciId}
      onThumbError={onPictureError}
    />
  );
  if (code) return cell;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      {freeTextMark}
      <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
    </div>
  );
};

export interface ApprovableLine extends PrefillInfo {
  catalogItem?: { id: number; partNumber: string; description?: string; descriptionAr?: string | null; unit?: { id: number; name: string } | null } | null;
  id: number;
  lineNo: number;
  partNumber?: string;
  itemDescription: string;
  qty: number | string;
  unitPrice: number | string;
  lineTotal: number | string;
  mrHeader: {
    id: number;
    mrId: string;
    department: { name: string; code: string };
  };
  vendor: { id: number; name: string } | null;
  category: { name: string } | null;
  unit: { name: string } | null;
  currency: { code: string } | null;
  costCenter?: any;
  /** Sent back by a PO line cancellation (2026-09-14): qty is what is left to order. */
  reorderFromPoId?: string | null;
  reorderReason?: string | null;
  originalQty?: number | string;
  /** When the line was approved: how long it has waited for a PO. */
  approvedOn?: string | null;
}

interface VendorGroup {
  vendorId: number | null;
  vendorName: string;
  lines: ApprovableLine[];
  total: number;
}

const POCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const isCompactLayout = useCompactLayout(); // phones + small tablets (2026-10-19)
  const [lines, setLines] = useState<ApprovableLine[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState('');
  const [reviewed, setReviewed] = useState(false);
  // 2026-10-19 (Ahmed D3-a): prices the officer gives lines that are still at 0, typed or "Use usual price".
  const [enteredPrices, setEnteredPrices] = useState<Record<number, { price: number | null; usual: boolean }>>({});
  // Search / narrowing (2026-09-08): one box over suppliers and lines, plus a supplier filter.
  const [query, setQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const searchRef = useRef<any>(null);
  const { isProcurement, isAdmin, isApprover } = useAuth();
  const canRefine = isProcurement || isAdmin;
  // MR line cancellation from Create PO (2026-09-15): CEO/admin cancel directly, procurement asks the CEO.
  const canCancelDirect = isApprover || isAdmin;
  const [cancelBusy, setCancelBusy] = useState(false);
  // Bulk: cancel (or request cancellation of) every ticked line with one reason.
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  // Item pictures (2026-09-22) for the approved lines waiting to be ordered.
  const lineItemIds = useMemo(() => lines.map((l) => l.catalogItem?.id ?? null), [lines]);
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);
  const pictureOf = (l: ApprovableLine) => (l.catalogItem?.id ? linePictures[l.catalogItem.id]?.url ?? null : null);
  const onPictureError = (l: ApprovableLine) =>
    l.catalogItem?.id ? () => reloadLinePicture(l.catalogItem!.id) : undefined;
  const submitBulkCancel = async () => {
    const reason = bulkReason.trim();
    if (!reason) { message.warning('A reason is required'); return; }
    const targets = lines.filter((l) => selectedIds.has(l.id));
    setCancelBusy(true);
    let done = 0;
    const failed: string[] = [];
    try {
      for (const l of targets) {
        const mrId = l.mrHeader?.mrId;
        try {
          if (canCancelDirect) await decideMRLineCancel(mrId, l.id, { decision: 'approve', reason });
          else await requestMRLineCancel(mrId, l.id, reason);
          done += 1;
        } catch (err: any) {
          failed.push(`${mrId} line ${l.lineNo}: ${err.response?.data?.error || 'failed'}`);
        }
      }
      if (done > 0) {
        message.success(canCancelDirect
          ? `${done} line${done === 1 ? '' : 's'} cancelled`
          : `${done} line${done === 1 ? '' : 's'} sent to the CEO for approval`);
      }
      if (failed.length > 0) message.error(failed.join(' · '), 8);
      setSelectedIds(new Set());
      setBulkCancelOpen(false);
      setBulkReason('');
      if (isCompactLayout) reloadQuiet(); else fetchLines();
    } finally {
      setCancelBusy(false);
    }
  };
  const [refineLine, setRefineLine] = useState<ApprovableLine | null>(null);
  const [refineOpen, setRefineOpen] = useState(false);

  const loadLines = async () => {
    const data = await getApprovableLines();
    setLines(Array.isArray(data) ? data : data.data || []);
  };
  const fetchLines = async () => {
      setLoading(true);
      try {
        await loadLines();
      } catch (err) {
        message.error('Failed to load approved lines');
      } finally {
        setLoading(false);
      }
    };
  // Reload without the full-page spinner, so the phone screen keeps the pile or supplier it has open.
  const reloadQuiet = async () => {
    try { await loadLines(); } catch { message.error('Failed to reload approved lines'); }
  };
  useEffect(() => { fetchLines(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Group lines by vendor
  const vendorGroups: VendorGroup[] = React.useMemo(() => {
    const groups: Map<string, VendorGroup> = new Map();
    lines.forEach((line) => {
      const key = line.vendor?.id ? String(line.vendor.id) : 'unassigned';
      if (!groups.has(key)) {
        groups.set(key, {
          vendorId: line.vendor?.id || null,
          vendorName: line.vendor?.name || 'Unassigned Vendor',
          lines: [],
          total: 0,
        });
      }
      const group = groups.get(key)!;
      group.lines.push(line);
      group.total += Number(line.lineTotal) || (Number(line.qty) * Number(line.unitPrice));
    });
    return Array.from(groups.values()).sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  }, [lines]);

  // Which fields the search box looks at, per line. Supplier name/code match every line of the group.
  const lineMatches = (line: ApprovableLine, q: string) =>
    catMatches(
      q,
      line.mrHeader?.mrId,
      line.catalogItem?.partNumber || line.partNumber,
      line.catalogItem?.description || line.itemDescription,
      line.catalogItem?.descriptionAr,
      line.costCenter ? costCenterLabel(line.costCenter) : null,
      line.vendor?.name,
      (line.vendor as any)?.appCode,
    );

  const trimmedQuery = query.trim();
  const filteredGroups: VendorGroup[] = React.useMemo(() => {
    const out: VendorGroup[] = [];
    for (const group of vendorGroups) {
      const groupKey = group.vendorId ? String(group.vendorId) : 'unassigned';
      // "Show selected only" shows every selected line, ignoring the search and supplier filter.
      if (!showSelectedOnly && vendorFilter && vendorFilter !== groupKey) continue;
      const visible = group.lines.filter((l) =>
        showSelectedOnly ? selectedIds.has(l.id) : !trimmedQuery || lineMatches(l, trimmedQuery),
      );
      if (visible.length === 0) continue;
      out.push({
        ...group,
        lines: visible,
        total: visible.reduce((sum, l) => sum + (Number(l.lineTotal) || Number(l.qty) * Number(l.unitPrice)), 0),
      });
    }
    return out;
  }, [vendorGroups, vendorFilter, trimmedQuery, showSelectedOnly, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleLineCount = filteredGroups.reduce((n, g) => n + g.lines.length, 0);
  const visibleIds = new Set(filteredGroups.flatMap((g) => g.lines.map((l) => l.id)));
  const hiddenSelectedCount = Array.from(selectedIds).filter((id) => !visibleIds.has(id)).length;
  const isNarrowed = !!trimmedQuery || !!vendorFilter || showSelectedOnly;

  // "/" focuses the search, Escape clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = searchRef.current?.input as HTMLInputElement | undefined;
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) { e.preventDefault(); el?.focus(); }
      if (e.key === 'Escape' && target === el) { setQuery(''); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A PO line never goes out at 0 (Ahmed D3-a, 2026-10-19): every selected line needs its own price or one given here.
  const hasPrice = (l: ApprovableLine) => Number(l.unitPrice) > 0;
  const givenPrice = (l: ApprovableLine) => Number(enteredPrices[l.id]?.price) > 0 ? Number(enteredPrices[l.id]?.price) : 0;
  const lineValue = (l: ApprovableLine) => hasPrice(l) ? (Number(l.lineTotal) || Number(l.qty) * Number(l.unitPrice)) : Number(l.qty) * givenPrice(l);
  const selectedLines = lines.filter((l) => selectedIds.has(l.id));
  const selectedUnpriced = selectedLines.filter((l) => !hasPrice(l));
  const stillUnpriced = selectedUnpriced.filter((l) => !givenPrice(l));
  const canUseUsual = stillUnpriced.filter((l) => usualPrice(l) != null);
  const setGiven = (id: number, price: number | null, usual: boolean) => setEnteredPrices((p) => ({ ...p, [id]: { price, usual } }));
  const useUsualOnAll = () => setEnteredPrices((p) => {
    const next = { ...p };
    canUseUsual.forEach((l) => { next[l.id] = { price: usualPrice(l), usual: true }; });
    return next;
  });
  const selectedTotal = selectedLines.reduce((sum, l) => sum + lineValue(l), 0);

  const toggleLine = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleVendorGroup = (vendorLines: ApprovableLine[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      vendorLines.forEach((l) => {
        if (checked) {
          next.add(l.id);
        } else {
          next.delete(l.id);
        }
      });
      return next;
    });
  };

  // GRN phase 1 (2026-10-21, g02): Create PO first asks how it was bought — nothing pre-selected, one supplier at a time.
  const [pathOpen, setPathOpen] = useState(false);
  const [directOpen, setDirectOpen] = useState(false);
  const selectedVendorIds = Array.from(new Set(selectedLines.map((l) => l.vendor?.id ?? 0)));
  const openPathChoice = () => {
    if (selectedIds.size === 0) { message.warning('Please select lines to create a PO'); return; }
    if (!reviewed) { message.warning('Please confirm the review checkbox before creating the PO'); return; }
    if (stillUnpriced.length > 0) {
      message.warning(`${stillUnpriced.length} selected line${stillUnpriced.length === 1 ? ' has' : 's have'} no price. Enter a price or use the usual price first.`);
      return;
    }
    if (selectedVendorIds.length !== 1 || !selectedVendorIds[0]) {
      message.warning("Choose one supplier's lines at a time: how it was bought is recorded per supplier.");
      return;
    }
    setPathOpen(true);
  };
  const directLines: DirectLine[] = selectedLines.map((l) => {
    const reorder = Number((l as any).reorderQty || 0);
    return {
      mrLineId: l.id,
      mrId: l.mrHeader?.mrId,
      lineNo: l.lineNo,
      asked: reorder > 0 ? reorder : Number(l.qty),
      unit: l.unit?.name || null,
      price: hasPrice(l) ? Number(l.unitPrice) : givenPrice(l) || null,
      costCenter: l.costCenter ? { id: l.costCenter.id, code: l.costCenter.code, name: l.costCenter.name } : null,
      item: renderItem(l, undefined, pictureOf(l), onPictureError(l)),
    };
  });

  const handleCreate = async (expectedDelivery?: string) => {
    if (selectedIds.size === 0) {
      message.warning('Please select lines to create a PO');
      return;
    }
    if (!reviewed) {
      message.warning('Please confirm the review checkbox before creating the PO');
      return;
    }

    if (stillUnpriced.length > 0) {
      message.warning(`${stillUnpriced.length} selected line${stillUnpriced.length === 1 ? ' has' : 's have'} no price. Enter a price or use the usual price first.`);
      return;
    }

    setCreating(true);
    try {
      // Prices given here are saved on the MR line through Refine (originals kept, note recorded), then the PO is created.
      for (const l of selectedUnpriced) {
        const given = enteredPrices[l.id];
        const hint = historyHint(l);
        await refineMRLine(l.id, {
          unitPrice: givenPrice(l),
          refinementNotes: given?.usual ? `Priced on Create PO with the usual price from purchase history${hint ? ` (${hint})` : ''}` : 'Priced on Create PO',
        });
      }
      // GRN phase 1 (2026-10-21): "Order from vendor" creates the PO as Ordered with its expected delivery date.
      const result = expectedDelivery
        ? await createVendorOrder(Array.from(selectedIds), expectedDelivery, notes || undefined)
        : await createPO(Array.from(selectedIds), notes || undefined, true);
      // result is an array of POs (one per vendor); navigate to the first one
      const firstPO = Array.isArray(result) ? result[0] : result;
      const poDbId = firstPO.id;
      message.success('Purchase Order created successfully');
      navigate(`/pos/${poDbId}`);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create PO');
      fetchLines(); // prices already saved stay saved; show them
    } finally {
      setCreating(false);
    }
  };

  const lineColumns = [
    {
      title: '',
      width: 50,
      render: (_: any, record: ApprovableLine) =>
        record.vendor?.id ? (
          <Checkbox
            checked={selectedIds.has(record.id)}
            onChange={(e) => toggleLine(record.id, e.target.checked)}
          />
        ) : (
          <Tooltip title="No supplier on this line. Refine it and choose a supplier before it can go on a PO.">
            <Checkbox disabled />
          </Tooltip>
        ),
    },
    {
      title: 'MR',
      key: 'mrId',
      width: 150,
      render: (_: any, record: ApprovableLine) => {
        const status = (record.mrHeader as any)?.status as string | undefined;
        const pendingSiblings = status === 'Partially Approved' || status === 'Submitted';
        return (
          <Space direction="vertical" size={0}>
            <span>{highlightText(record.mrHeader?.mrId || '-', trimmedQuery)}</span>
            {status && (
              <Tooltip title={pendingSiblings ? 'Other lines on this MR are still waiting for approval' : undefined}>
                <Tag color={pendingSiblings ? 'orange' : 'default'} style={{ fontSize: 11, lineHeight: '16px', margin: 0 }}>
                  {status}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Line #',
      dataIndex: 'lineNo',
      width: 70,
      align: 'center' as const,
    },
    {
      // Flexible on purpose: the code must never be clipped or wrapped mid-code.
      title: 'Item',
      key: 'item',
      render: (_: any, record: ApprovableLine) => (
        <div>
          {renderItem(record, trimmedQuery, pictureOf(record), onPictureError(record))}
          {record.reorderFromPoId && (
            <div style={{ fontSize: 11.5, color: '#9a5b00', marginTop: 2 }}>
              back from {record.reorderFromPoId}{record.reorderReason ? ` · ${record.reorderReason}` : ''} · {Number(record.qty)} of {Number(record.originalQty)} to order again
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      width: 150,
      render: (_: any, record: ApprovableLine) => (
        <VendorCell
          name={record.vendor?.name}
          appCode={(record.vendor as any)?.appCode}
          missingLabel="unassigned"
          highlight={trimmedQuery}
        />
      ),
    },
    {
      title: 'Cost Center',
      key: 'costCenter',
      width: 180,
      ellipsis: true,
      render: (_: any, record: ApprovableLine) => record.costCenter ? highlightText(costCenterLabel(record.costCenter), trimmedQuery) : <Tag>None</Tag>,
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: 70,
      align: 'right' as const,
      render: (val: any) => Number(val),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 150,
      align: 'right' as const,
      render: (val: any, record: ApprovableLine) => {
        if (Number(val) > 0) return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 });
        const hint = historyHint(record);
        return (
          <div style={{ textAlign: 'right' }}>
            <Tag color="red" style={{ marginRight: 0 }}>no price</Tag>
            <div style={{ fontSize: 11, color: '#8a94a6', lineHeight: 1.35 }}>{hint || 'no purchase history'}</div>
          </div>
        );
      },
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 80,
      render: (_: any, record: ApprovableLine) => record.currency?.code || '-',
    },
    {
      title: 'Total',
      key: 'total',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: ApprovableLine) =>
        lineValue(record).toLocaleString('en-US', {
          minimumFractionDigits: 2,
        }),
    },
    ...(canRefine ? [{
      title: '',
      key: 'refine',
      width: 110,
      render: (_: any, record: ApprovableLine) => (
        <Button size="small" icon={<ToolOutlined />} onClick={() => { setRefineLine(record); setRefineOpen(true); }}>
          {record.catalogItem ? 'Refine' : 'Assign code'}
        </Button>
      ),
    }] : []),
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/pos'))}>
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Create Purchase Order
            </Title>
          </Space>
        </Col>
      </Row>

      {lines.length === 0 ? (
        <Card>
          <Empty description="No approved lines available for ordering" />
        </Card>
      ) : isCompactLayout ? (
        // Phones + small tablets (2026-10-21, P1-a): three piles instead of the long list.
        <POCreatePhone
          lines={lines}
          matches={(l) => !trimmedQuery || lineMatches(l, trimmedQuery)}
          query={query}
          setQuery={setQuery}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          enteredPrices={enteredPrices}
          setGiven={setGiven}
          reviewed={reviewed}
          setReviewed={setReviewed}
          notes={notes}
          setNotes={setNotes}
          creating={creating}
          onCreate={openPathChoice}
          canRefine={canRefine}
          onRefine={(l) => { setRefineLine(l); setRefineOpen(true); }}
          cancelLabel={canRefine || canCancelDirect ? (canCancelDirect ? 'Cancel ticked lines' : 'Ask to cancel ticked lines') : null}
          onCancelSelected={() => { setBulkReason(''); setBulkCancelOpen(true); }}
          reload={reloadQuiet}
          renderItem={(l) => renderItem(l, trimmedQuery, pictureOf(l), onPictureError(l))}
        />
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Select approved MR lines to create a Purchase Order. Lines should be from the same vendor.
          </Text>

          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              background: '#f5f6fa',
              padding: '4px 0 10px',
              marginBottom: 6,
            }}
          >
            <Row gutter={[8, 8]} align="middle" wrap={isCompactLayout}>
              <Col flex={isCompactLayout ? '1 1 100%' : 'auto'}>
                <Input
                  ref={searchRef}
                  allowClear
                  size="large"
                  prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
                  suffix={!trimmedQuery ? <Text type="secondary" style={{ fontSize: 11, border: '1px solid #e3e6ec', borderRadius: 4, padding: '0 5px' }}>/</Text> : null}
                  placeholder="Search supplier, MR number, item code, description (English or Arabic), cost center…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </Col>
              <Col flex={isCompactLayout ? '1 1 100%' : '230px'}>
                <Select
                  allowClear
                  size="large"
                  style={{ width: '100%' }}
                  placeholder="All suppliers"
                  value={vendorFilter ?? undefined}
                  onChange={(v) => setVendorFilter(v ?? null)}
                  showSearch
                  optionFilterProp="label"
                  options={vendorGroups.map((g) => ({
                    value: g.vendorId ? String(g.vendorId) : 'unassigned',
                    label: `${g.vendorName} (${g.lines.length})`,
                  }))}
                />
              </Col>
              <Col flex="none">
                <Text type="secondary" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Text strong>{visibleLineCount}</Text> lines · <Text strong>{filteredGroups.length}</Text> suppliers
                  {isNarrowed && (
                    <>
                      {' '}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => { setQuery(''); setVendorFilter(null); setShowSelectedOnly(false); }}
                      >
                        show all
                      </Button>
                    </>
                  )}
                </Text>
              </Col>
              {(canRefine || canCancelDirect) && (
                <Col flex="none">
                  <Tooltip title={selectedIds.size === 0 ? 'Tick the lines to cancel first' : (canCancelDirect ? 'Cancel the ticked lines; they leave Create PO and stay on their MRs as history' : 'Ask the CEO to cancel the ticked lines')}>
                    <Button
                      size="large"
                      danger
                      icon={<StopOutlined />}
                      disabled={selectedIds.size === 0}
                      onClick={() => { setBulkReason(''); setBulkCancelOpen(true); }}
                    >
                      {canCancelDirect ? 'Cancel lines' : 'Request cancellation'}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                    </Button>
                  </Tooltip>
                </Col>
              )}
            </Row>
          </div>

          {filteredGroups.length === 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Empty
                description={
                  showSelectedOnly
                    ? 'No lines selected yet.'
                    : 'No approved lines match. Clear the search or pick another supplier.'
                }
              />
            </Card>
          )}

          {filteredGroups.map((group) => {
            const fullCount = vendorGroups.find((g) => g.vendorId === group.vendorId)?.lines.length ?? group.lines.length;
            const selectable = group.lines.filter((l) => !!l.vendor?.id);
            const allSelected = selectable.length > 0 && selectable.every((l) => selectedIds.has(l.id));
            const someSelected = selectable.some((l) => selectedIds.has(l.id));

            return (
              <Card
                key={group.vendorId || 'unassigned'}
                title={
                  <Space>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => toggleVendorGroup(selectable, e.target.checked)}
                      disabled={selectable.length === 0}
                    />
                    <Text strong>{highlightText(group.vendorName, trimmedQuery)}</Text>
                    <Tag>{group.lines.length === fullCount ? `${group.lines.length} lines` : `${group.lines.length} of ${fullCount} lines`}</Tag>
                    <Text type="secondary">
                      Total: {group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Text>
                  </Space>
                }
                style={{ marginBottom: 16 }}
                size="small"
              >
                <ResponsiveTable
                  card={{ header: ['item'], body: ['mrId', 'lineNo', 'qty', 'unitPrice', 'total', 'costCenter'], hidden: ['vendor', 'currency'], footer: ['refine'] }}
                  columns={lineColumns}
                  dataSource={group.lines}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 1496 }}
                />
              </Card>
            );
          })}

          {selectedIds.size > 0 && (
            <Card
              title="Review — Confirm qty, unit, and price for each selected line"
              style={{ marginTop: 16, borderColor: '#faad14' }}
              size="small"
            >
              {selectedUnpriced.length > 0 && (
                <Alert
                  type={stillUnpriced.length > 0 ? 'warning' : 'success'}
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    <div>
                      {stillUnpriced.length > 0
                        ? <span><b>{stillUnpriced.length} selected line{stillUnpriced.length === 1 ? ' has' : 's have'} no price.</b> Enter a price, or use the usual price where we have one, before creating the PO.</span>
                        : <span>All selected lines are priced. The prices you gave are saved on the MR lines (Refine) when the PO is created.</span>}
                      {/* Button under the text, not in Alert's action slot: on a phone the action slot squeezed the text to one letter per line. */}
                      {canUseUsual.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Button size="small" onClick={useUsualOnAll} style={{ whiteSpace: 'normal', height: 'auto', textAlign: 'left' }}>
                            Use usual price on the {canUseUsual.length} line{canUseUsual.length === 1 ? '' : 's'} that have one
                          </Button>
                        </div>
                      )}
                    </div>
                  }
                />
              )}
              <ResponsiveTable
                card={{ header: ['item'], body: ['mr', 'qty', 'unit', 'unitPrice', 'lineTotal'] }}
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={lines.filter((l) => selectedIds.has(l.id))}
                columns={[
                  {
                    title: 'MR',
                    key: 'mr',
                    width: 110,
                    render: (_: any, r: ApprovableLine) => r.mrHeader?.mrId || '-',
                  },
                  { title: 'Line #', dataIndex: 'lineNo', width: 70, align: 'center' as const },
                  {
                    title: 'Item',
                    key: 'item',
                    render: (_: any, r: ApprovableLine) => renderItem(r, undefined, pictureOf(r), onPictureError(r)),
                  },
                  {
                    title: 'Qty',
                    dataIndex: 'qty',
                    width: 80,
                    align: 'right' as const,
                    render: (v: any) => Number(v),
                  },
                  {
                    title: 'Unit',
                    key: 'unit',
                    width: 90,
                    render: (_: any, r: ApprovableLine) => r.unit?.name || '-',
                  },
                  {
                    title: 'Unit Price',
                    dataIndex: 'unitPrice',
                    width: 230,
                    align: 'right' as const,
                    render: (v: any, r: ApprovableLine) => {
                      if (Number(v) > 0) return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 });
                      const usual = usualPrice(r);
                      const hint = historyHint(r);
                      return (
                        <div style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                          <InputNumber
                            id={`po-price-${r.id}`}
                            min={0}
                            step={1}
                            precision={2}
                            placeholder="0.00"
                            status={givenPrice(r) ? undefined : 'error'}
                            style={{ width: 120 }}
                            value={enteredPrices[r.id]?.price ?? undefined}
                            onChange={(val) => setGiven(r.id, val == null ? null : Number(val), false)}
                          />
                          {hint
                            ? <span style={{ fontSize: 11, color: '#8a94a6', lineHeight: 1.35, textAlign: 'right' }}>{hint}</span>
                            : <span style={{ fontSize: 11, color: '#cf1322' }}>no purchase history, enter a price</span>}
                          {usual != null && (
                            <Button size="small" onClick={() => setGiven(r.id, usual, true)}>
                              Use {usual.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                            </Button>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Line Total',
                    key: 'lineTotal',
                    width: 120,
                    align: 'right' as const,
                    render: (_: any, r: ApprovableLine) =>
                      lineValue(r).toLocaleString('en-US', { minimumFractionDigits: 2 }),
                  },
                ]}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Checkbox
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
              >
                <Text strong>
                  I have reviewed each line total and confirm the quantities, units, and prices
                  are correct
                </Text>
              </Checkbox>
            </Card>
          )}

          <Card style={{ marginTop: 16 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <TextArea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="PO notes (optional)"
                  style={{ maxWidth: 500 }}
                />
              </Col>
              <Col>
                <Space direction="vertical" align="end">
                  <Statistic
                    title="Selected Lines"
                    value={selectedIds.size}
                    suffix={`/ ${lines.length}`}
                  />
                  {hiddenSelectedCount > 0 && (
                    <Tag color="orange" style={{ margin: 0 }}>
                      {hiddenSelectedCount} selected line{hiddenSelectedCount > 1 ? 's' : ''} hidden by the search
                    </Tag>
                  )}
                  {selectedIds.size > 0 && (
                    <Button size="small" onClick={() => setShowSelectedOnly((v) => !v)}>
                      {showSelectedOnly ? 'Show all lines' : 'Show selected only'}
                    </Button>
                  )}
                  <Statistic
                    title="Selected Total"
                    value={selectedTotal}
                    precision={2}
                    prefix="$"
                  />
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined />}
                    loading={creating}
                    onClick={openPathChoice}
                    disabled={selectedIds.size === 0 || !reviewed || stillUnpriced.length > 0}
                  >
                    Create Purchase Order
                  </Button>
                  {stillUnpriced.length > 0 && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {stillUnpriced.length} line{stillUnpriced.length === 1 ? ' still needs' : 's still need'} a price
                    </Text>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </>
      )}
      <Modal
        title={`${canCancelDirect ? 'Cancel' : 'Request cancellation of'} ${selectedIds.size} selected line${selectedIds.size === 1 ? '' : 's'}`}
        open={bulkCancelOpen}
        onOk={submitBulkCancel}
        onCancel={() => setBulkCancelOpen(false)}
        confirmLoading={cancelBusy}
        okText={canCancelDirect ? 'Cancel lines' : 'Send to CEO'}
        okButtonProps={{ danger: true }}
        cancelText="Keep them"
        width={640}
        destroyOnClose
      >
        <Text type="secondary">
          {canCancelDirect
            ? 'They leave Create PO and stay on their MRs as history; nothing is deleted.'
            : 'Goes to the CEO for approval. The lines are held out of Create PO until decided.'}
        </Text>
        <div style={{ maxHeight: 260, overflowY: 'auto', margin: '12px 0', border: '1px solid #e3e6ec', borderRadius: 6 }}>
          {lines.filter((l) => selectedIds.has(l.id)).map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 10px', borderBottom: '1px solid #f0f1f4', fontSize: 13 }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ color: '#8a94a6', marginRight: 8 }}>{l.mrHeader?.mrId} · {l.lineNo}</span>
                {l.catalogItem?.partNumber || l.partNumber ? <code style={{ marginRight: 6 }}>{l.catalogItem?.partNumber || l.partNumber}</code> : null}
                {l.catalogItem?.description || l.itemDescription}
              </span>
              <span style={{ whiteSpace: 'nowrap', color: '#4b5563' }}>{Number(l.qty)} {l.unit?.name || ''}</span>
            </div>
          ))}
        </div>
        <div><Text strong>Reason</Text> <Text type="danger">*</Text> <Text type="secondary" style={{ fontSize: 12 }}>(same reason on every line)</Text></div>
        <TextArea
          rows={3}
          maxLength={500}
          value={bulkReason}
          onChange={(e) => setBulkReason(e.target.value)}
          placeholder="Why these lines are no longer needed"
          style={{ marginTop: 4 }}
        />
      </Modal>
      <PathChoice
        open={pathOpen}
        vendorId={selectedVendorIds[0] || null}
        vendorName={selectedLines[0]?.vendor?.name || ''}
        summary={`${selectedLines.length} line${selectedLines.length === 1 ? '' : 's'} · ${selectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EGP`}
        busy={creating}
        onCancel={() => setPathOpen(false)}
        onDirect={() => { setPathOpen(false); setDirectOpen(true); }}
        onOrder={(date) => { setPathOpen(false); handleCreate(date); }}
      />
      <DirectPurchase
        open={directOpen}
        vendorName={selectedLines[0]?.vendor?.name || ''}
        lines={directLines}
        onCancel={() => setDirectOpen(false)}
        onSaved={(res) => { setDirectOpen(false); navigate(`/grns/${res.grn.grnId}`); }}
      />
      <RefineLineModal
        line={refineLine as any}
        open={refineOpen}
        onClose={() => { setRefineOpen(false); setRefineLine(null); }}
        onSaved={isCompactLayout ? reloadQuiet : fetchLines}
        departmentId={(refineLine as any)?.mrHeader?.department?.id ?? (refineLine as any)?.mrHeader?.departmentId ?? null}
      />
    </div>
  );
};

export default POCreate;
